import { EChartsOption } from "echarts";
import { Box, Container } from "@mui/material";
import { RopChart } from "../../../../main/components/RopChart";
import { numberPtBrPercent01 } from "../../../../main/utils/utils";
import { EmptyTable } from "../../../clients/components/EmptyState";
import { InverterPerformance } from "../../types";

export default function InverterPerformanceCard(props: {
  performanceInversores: InverterPerformance[];
}) {
  const { performanceInversores } = props;
  const m = performanceInversores.reduce(
    (acc, item) => {
      acc.sum += item.measurement;
      acc.count++;

      if (acc.min === null || item.measurement < acc.min) {
        acc.min = item.measurement;
      }
      if (acc.max === null || item.measurement > acc.max) {
        acc.max = item.measurement;
      }
      return acc;
    },
    {
      sum: 0,
      count: 0,
      min: null as number | null,
      max: null as number | null,

      get avg(): number {
        if (this.count === 0) {
          return 0;
        }
        return this.sum / this.count;
      },
    }
  );

  const performanceDosInversores = {
    potenciaMediaDiaEsperada: m?.avg ?? 0,
    potenciaMediaDiaMinima: m?.min ?? 0,
    potenciaMediaDiaMaxima: m?.max ?? 0,

    potenciaMediaDiaInversores: performanceInversores
      .map((item) => ({
        nome: item.name,
        potenciaMediaDia: item.measurement,
        desempenho: item.desempenho,
      }))
      .sort((a, b) => {
        // Função auxiliar para extrair números do final dos nomes
        const extractNumber = (name: string) =>
          parseInt(name.match(/\d+$/)?.[0] || "0", 10);

        // Extrai os números do final dos nomes
        const numberA = extractNumber(a.nome);
        const numberB = extractNumber(b.nome);

        // Verifica se o nome de 'a' começa com "UG.1"
        const isUG1A = a.nome.startsWith("UG.1");

        // Verifica se o nome de 'b' começa com "UG.1"
        const isUG1B = b.nome.startsWith("UG.1");

        // Comparação
        if (isUG1A && !isUG1B) {
          return -1; // Coloca 'a' antes de 'b'
        } else if (!isUG1A && isUG1B) {
          return 1; // Coloca 'b' antes de 'a'
        } else {
          // Se ambos começarem ou não começarem com "UG.1", compara os números
          return numberA - numberB;
        }
      }),
  };

  const xData = performanceDosInversores.potenciaMediaDiaInversores?.map(
    (item) => item.nome
  );

  const placeholderData =
    performanceDosInversores.potenciaMediaDiaInversores?.map((item) => {
      const centro = performanceDosInversores.potenciaMediaDiaEsperada;
      const valor = item.potenciaMediaDia;

      const distanciaCentro = valor - centro;

      if (distanciaCentro >= 0) {
        return centro;
      }

      return valor;
    });

  const seriesBaseData =
    performanceDosInversores.potenciaMediaDiaInversores?.map(
      (item, idx, arr) => ({
        name: item.nome,
        data: Array.from({ length: arr.length }, (_, i2) => {
          if (i2 !== idx) {
            return "-";
          }

          const centro = performanceDosInversores.potenciaMediaDiaEsperada;
          const valor = item.potenciaMediaDia;
          const distanciaCentro = valor - centro;
          return Math.abs(distanciaCentro);
        }),

        value: item.potenciaMediaDia,
        label: numberPtBrPercent01(item.desempenho, 0),

        acimaDoEsperado:
          item.potenciaMediaDia >=
          performanceDosInversores.potenciaMediaDiaEsperada,
      })
    );

  const okColor = "#FADE0A";
  const notOkColor = "#EE7D00";

  function getValue() {
    const serie = new Array();

    for (const item of seriesBaseData ?? []) {
      serie.push({
        name: item.name,
        type: "bar",
        stack: "Total",
        label: {
          show: true,
          position: item.acimaDoEsperado ? "top" : "bottom",
          color: item.acimaDoEsperado ? okColor : notOkColor,
          fontSize: 9,
          formatter() {
            return item.label;
          },
        },
        itemStyle: {
          color: item.acimaDoEsperado ? okColor : notOkColor,
        },
        data: item.data,
      });
    }
    return serie;
  }

  const hasData =
    performanceDosInversores.potenciaMediaDiaEsperada ||
    performanceDosInversores.potenciaMediaDiaMaxima ||
    performanceDosInversores.potenciaMediaDiaMinima ||
    (performanceDosInversores.potenciaMediaDiaInversores?.length ?? 0 > 0);

  const options: EChartsOption = {
    title: {
      text: "Performance dos Inversores (Potência Ativa kW)",
      textStyle: {
        fontSize: 12,
      },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter(params: any) {
        const tar1 = params[1];
        const tar2 = params[2];

        if (tar1.value !== "-") {
          const tar1ValuePtBr =
            typeof tar1.value === "number"
              ? tar1.value.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : `${tar1.value}`;
          return `${tar1.name}<br/>medição : ${tar1ValuePtBr}`;
        } else {
          const tar2ValuePtBr =
            typeof tar2.value === "number"
              ? tar2.value.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : `${tar2.value}`;
          return `${tar2.name}<br/>${tar2.seriesName} : -${tar2ValuePtBr}`;
        }
      },
    },
    backgroundColor: "transparent",
    grid: {
      left: 0,
      right: 45,
      bottom: 0,
      top: 30,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: xData,

      axisLabel: {
        fontSize: 8,
        rotate: 45,
      },
    },
    yAxis: {
      type: "value",
      nameTextStyle: {
        padding: [0, 0, -3, 0],
      },

      ...(!!performanceDosInversores.potenciaMediaDiaMinima && {
        min: Math.floor(performanceDosInversores.potenciaMediaDiaMinima - 4),
      }),
      ...(!!performanceDosInversores.potenciaMediaDiaMaxima && {
        max: Math.ceil(performanceDosInversores.potenciaMediaDiaMaxima + 4),
      }),

      axisLabel: {
        fontSize: 9,
        padding: [0, -5, 0, 0],
        formatter(value: number) {
          return value.toFixed(0);
        },
      },
    },
    series: [
      {
        name: "Média",
        type: "line",
        endLabel: {
          show: true,
          fontSize: 10,
          formatter: function (params) {
            return Number(params.value).toFixed(1) + " kW";
          },
        },
        data: Array.from({ length: xData?.length ?? 0 }, () => m?.avg ?? 0),
        lineStyle: {
          color: "#000",
          type: "dashed",
          width: 1,
        },
        symbol: "none",
      },
      {
        name: "Placeholder",
        type: "bar",
        stack: "Total",
        silent: true,
        itemStyle: {
          borderColor: "transparent",
          color: "transparent",
        },
        emphasis: {
          itemStyle: {
            borderColor: "transparent",
            color: "transparent",
          },
        },
        data: placeholderData,
      },
      ...getValue(),
    ],
  };

  return (
    <>
      {hasData ? (
        <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "90%",
            width: "100%",
          }}
        >
          <RopChart option={options} />
        </Container>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <EmptyTable title="Nenhum dado encontrado!" />
        </Box>
      )}
    </>
  );
}
